import { useCallback, useEffect, useRef, useState } from 'react';
import useMessaging from '../../../../../hooks/useMessaging';
import { AttendeeMessage, MessagingAttendee, RegistrationFieldFilter } from '../../../../../interfaces/interfaces';
import {
  fetchAndSetAttendeeMessaging,
  fetchAndSetMessagingAttendees,
  setAttendeeMessaging,
  setMessagingOpenConversation,
} from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import { fetchMessagingAttendeesService } from '../../../../../services/services';
import { getQueryParam } from '../../../../../utils/functions';
import { VyLoader } from '../../../../common/VyLoader';
import AttendeesList from './AttendeesList';
import ConversationComponent from './ConversationComponent';
import ConversationList from './ConversationList';
import MessagingBoothOpens from './MessagingBoothOpens';
import MessagingConfig from './MessagingConfig';
import MessagingContactshares from './MessagingContactshares';
import MessagingSearchField from './MessagingSearchField';
import MessagingSideMenu from './MessagingSideMenu';
const NBR_OF_ATTENDEES_PER_QUERY = 25;
const ATT_ANIMATION_DURATION = 300;
const ATT_ANIMATION_DELAY = 50;
export interface Conversation {
  messages: AttendeeMessage[];
  attendee?: MessagingAttendee;
}

const MessagingCenter = () => {
  const [selectedTab, setSelectedTab] = useState<0 | 1 | 2 | 3 | 4>(0);

  const [isLoading, setIsLoading] = useState(true);

  const searchTimeout = useRef<NodeJS.Timeout | undefined>();
  const [showSkeleton, setShowSkeleton] = useState(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const event = useSelector(({ common }) => common.event) ?? useSelector(({ video }) => video?.meeting?.booth.event);
  const user = useSelector(({ common }) => common.user);
  const messaging = useSelector(({ common }) => common.attendeeMessaging);
  const eventChannel = useSelector(({ common }) => common.pusherChannels.eventChannel);
  const messagingRef = useRef<typeof messaging>();
  const messagingOnlineFiltering = useSelector(({ common }) => common.messagingOnlineFiltering);
  const messagingShowHosts = useSelector(({ common }) => common.messagingShowHosts);
  const messagingSortByName = useSelector(({ common }) => common.messagingSortByName);
  const openAttendeeMessaging = useSelector(({ common }) => common.openAttendeeMessaging);
  const currentlyOpenConversation = useSelector(({ common }) => common.openConversation);
  const messagingRegistrationFilters = useSelector(({ common }) => common.messagingRegistrationFilters);
  const isMobilePreview = useSelector((state) => state.common.isMobilePreview);

  const { openMessagingConversationFromAttendee, rebuildConversations } = useMessaging();

  const dispatch = useDispatch();

  useEffect(() => {
    if (!event) {
      dispatch(setAttendeeMessaging(undefined));
      return;
    }

    setIsLoading(true);
    if (messaging) {
      setIsLoading(false);
    } else {
      dispatch(fetchAndSetAttendeeMessaging(event._id));

      dispatch(
        fetchAndSetMessagingAttendees(
          event._id,
          selectedTab === 1 && messagingOnlineFiltering,
          selectedTab !== 1 || messagingShowHosts,
          messagingRegistrationFilters,
          messagingSortByName
        )
      );
    }
  }, [messaging, openAttendeeMessaging]);

  useEffect(() => {
    if (!eventChannel) return;
    eventChannel.bind('messaging-attendee-update', attendeeUpdateCb);
    eventChannel.bind('user-presence-update', attendeePresenceUpdateCb);
    eventChannel.bind('cancel-or-delete-registration', onDeleteOrCancelRegistration);
    return () => {
      eventChannel.unbind('attendee-update');
      eventChannel.unbind('user-presence-update');
      eventChannel.unbind('cancel-or-delete-registration');
    };
  }, [eventChannel]);

  useEffect(() => {
    messagingRef.current = messaging;
  }, [messaging]);

  useEffect(() => {
    rebuildConversations();
  }, [messaging?.messages]);

  useEffect(() => {
    if (selectedTab < 2) {
      dispatch(setAttendeeMessaging(undefined));
      setIsLoading(false);
    }
  }, [selectedTab, messagingOnlineFiltering, messagingShowHosts, messagingSortByName]);

  const loadAttendees = async () => {
    if (!event) return;
    setIsLoading(true);
    const { data } = await fetchMessagingAttendeesService(event._id, undefined, undefined, messagingShowHosts);
    dispatch(setAttendeeMessaging({ ...messaging, attendees: [...data.attendees], attendeesCount: data.count }));
    setIsLoading(false);
  };

  const attendeeUpdateCb = useCallback(
    ({ ghostmode = false, attendee }: { ghostmode: boolean; attendee: MessagingAttendee }) => {
      if (!attendee) return;
      if (attendee._id === user?._id) return;
      const _attendees = messagingRef.current?.attendees ? [...messagingRef.current.attendees] : [];
      const attendeeIndex = _attendees.map((a) => a._id).indexOf(attendee._id);
      if (ghostmode) {
        if (attendeeIndex >= 0) _attendees.splice(attendeeIndex, 1);
      } else if (attendeeIndex < 0) _attendees.push(attendee);
      dispatch(setAttendeeMessaging({ ...messagingRef.current, attendees: _attendees }));
    },
    [messaging]
  );

  /**
   * @callback attendeePresenceUpdateCb
   * @param updates object of u: user id . s: status
   */
  const attendeePresenceUpdateCb = useCallback(
    (updates: { u: string; s: 0 | 1 }[]) => {
      if (!updates) return;
      let _attendees = messagingRef.current?.attendees ? [...messagingRef.current.attendees] : [];
      _attendees = _attendees.map((attendee) => {
        const _update = updates.find((update) => update.u === attendee._id);
        if (!_update) return attendee;
        if (_update.s === 1) return { ...attendee, datetimeLastRealtimeConnect: new Date() };
        else return { ...attendee, datetimeLastRealtimeDisconnect: new Date() };
      });
      dispatch(setAttendeeMessaging({ ...messagingRef.current, attendees: _attendees }));
    },
    [messaging]
  );

  const onDeleteOrCancelRegistration = () => {
    if (!event) return;
    dispatch(fetchAndSetMessagingAttendees(event!._id));
    dispatch(fetchAndSetAttendeeMessaging(event!._id));
  };

  const handleSearch = async (search: string) => {
    if (!event) return;
    if (search.length < 3 && search.length > 0) return;
    setIsLoading(true);
    searchTimeout?.current && clearTimeout(searchTimeout?.current);
    searchTimeout.current = setTimeout(async () => {
      const { data } = await fetchMessagingAttendeesService(event._id, 0, search, messagingOnlineFiltering, messagingRegistrationFilters, messagingShowHosts);
      dispatch(setAttendeeMessaging({ ...messaging, attendees: [...data.attendees], attendeesCount: data.count, isAttendeesFiltered: search.length > 0 }));
      searchTimeout.current = undefined;
      setIsLoading(false);
    }, 300);
  };

  const handleFilters = async (registrationFilters: RegistrationFieldFilter[]) => {
    if (!event) return;
    setIsLoading(true);
    const search = !searchInputRef?.current?.value ? '' : searchInputRef?.current?.value?.length < 3 ? '' : searchInputRef.current?.value;
    const { data } = await fetchMessagingAttendeesService(
      event._id,
      0,
      search,
      messagingOnlineFiltering,
      registrationFilters,
      messagingShowHosts,
      messagingSortByName
    );
    dispatch(setAttendeeMessaging({ ...messaging, attendees: [...data.attendees], attendeesCount: data.count, isAttendeesFiltered: search.length > 0 }));
    setIsLoading(false);
  };

  const loadMoreAttendees = async () => {
    if (!event) return;
    const search = searchInputRef?.current?.value && searchInputRef?.current?.value?.length < 3 ? '' : searchInputRef.current?.value;
    const { data } = await fetchMessagingAttendeesService(
      event._id,
      messaging?.attendees?.length,
      search,
      messagingOnlineFiltering,
      messagingRegistrationFilters,
      messagingShowHosts
    );

    // causes a commit error -> Type 'MessagingAttendee[] | undefined' is not an array type or does not have a '[Symbol.iterator]()' method that returns an iterator.
    // probably because messaging can be undefined, but it works anyways
    // @ts-ignore
    dispatch(setAttendeeMessaging({ ...messaging, attendees: [...messaging?.attendees, ...data.attendees], attendeesCount: data.count }));

    // hide the skeleton until the attendees animation is finished
    /* setTimeout(() => {
      setShowSkeleton(true);
    }, ATT_ANIMATION_DURATION + (NBR_OF_ATTENDEES_PER_QUERY - 1) * ATT_ANIMATION_DELAY);*/
  };

  const renderRightSide = () => {
    if (selectedTab === 0) return <ConversationList isLoading={isLoading} setSelectedTab={(index) => setSelectedTab(index)} />;
    if (selectedTab === 1)
      return (
        <AttendeesList
          attendees={messaging?.attendees}
          attendeesCount={messaging?.attendeesCount ?? 0}
          isLoading={false}
          showSkeleton={showSkeleton}
          setOpenConversation={(attendee) => openMessagingConversationFromAttendee(attendee)}
          loadMoreAttendees={loadMoreAttendees}
        />
      );

    if (selectedTab === 2)
      return (
        <div className="flex flex-col w-full h-full">
          <MessagingBoothOpens setOpenConversation={(attendee) => openMessagingConversationFromAttendee(attendee)} />
        </div>
      );
    if (selectedTab === 4) return <MessagingContactshares />;

    return <MessagingConfig />;
  };

  function handleTabChange(index: 0 | 1 | 2 | 3 | 4) {
    if (selectedTab === index) return;
    if (index < 2) {
      setIsLoading(true);
    }

    setSelectedTab(index);
    // if (index === 0 && searchInputRef?.current?.value && searchInputRef.current.value.length > 0) await loadAttendees();
  }

  return (
    <>
      <div className="relative h-full">
        {isLoading && (
          <div className="absolute flex items-center justify-center w-full h-full">
            <VyLoader onlyPartial />
          </div>
        )}
        <div
          className={`max-w-[1536px] h-full flex flex-col  ${isMobilePreview ? 'justify-start' : 'sm:flex-row'} text-gray-900`}
          style={{
            width: getQueryParam('useappview') === 'true' ? '100cqw' : '70cqw',
            height: getQueryParam('useappview') === 'true' ? '100cqh' : '70cqh',
          }}>
          {!currentlyOpenConversation && (
            <MessagingSideMenu selectedTab={selectedTab} setSelectedTab={handleTabChange} isOpenConversation={currentlyOpenConversation !== undefined} />
          )}
          <div className={`h-full w-full ${!!currentlyOpenConversation ? '' : ''} flex flex-col transition-all duration-500 ease-in-out`}>
            {currentlyOpenConversation !== undefined ? (
              <ConversationComponent conversation={currentlyOpenConversation} onReturnClick={() => dispatch(setMessagingOpenConversation(undefined))} />
            ) : (
              <>
                {selectedTab === 1 && (
                  <MessagingSearchField searchInputRef={searchInputRef} isLoading={isLoading} handleSearch={handleSearch} handleFilters={handleFilters} />
                )}
                <div className="flex flex-col w-full h-full overflow-auto">{renderRightSide()}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default MessagingCenter;
